'use client';

/**
 * Scratchpad — a slide-up "digital paper" a kid can pull out to work a problem by hand
 * instead of needing real paper (community request, Aug 2026).
 *
 * CENTRALIZED: <ScratchpadProvider> is mounted ONCE at the app root and owns the single
 * pad instance. A solve screen doesn't render the pad — it just calls useScratchpad()
 * (one line) to reveal the ✏️ while that screen is open. One component, one instance,
 * one place — never scattered, never duplicated.
 *
 * Deliberately DUMB: pure ephemeral scratch paper, Ms. Emma never sees it. (An "Emma
 * reads your work" layer is intentionally NOT here — for graded practice that would kill
 * the productive struggle; her eyes belong in teaching moments, not answer-checking.)
 *
 * Ink: perfect-freehand (the same engine tldraw/Excalidraw use) rendered on a raw
 * <canvas> for touch/stylus/mouse. Tuned for WRITING digits (low thinning, heavy
 * streamline) rather than artistic brushwork. Lines and shapes are plain canvas strokes
 * (drag from corner to corner), for number lines, fraction bars and diagrams.
 *
 * Community request, Sep 2026: more colors and tools — pen sizes, any color via a
 * picker, lined / grid / blank paper, and line / rectangle / circle tools.
 */

import { createContext, useCallback, useContext, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { getStroke } from 'perfect-freehand';
import { Pencil, Highlighter, Eraser, Undo2, Trash2, X, Slash, Square, Circle, AlignJustify, Grid3x3, File, ChevronDown } from 'lucide-react';

type Tool = 'pen' | 'highlight' | 'eraser' | 'line' | 'rect' | 'circle';
type Paper = 'lined' | 'grid' | 'blank';
interface Stroke { tool: Tool; color: string; size: number; points: number[][]; }

// Quick-pick swatches; the rainbow swatch after them opens a full color picker.
const INK = ['#2b2b2b', '#2f6fe0', '#d11414']; // black / blue / red
const HIGHLIGHT = ['#ffd54a', '#7ee081', '#ff8fc7', '#7cc7ff']; // yellow / green / pink / blue

const isShape = (t: Tool) => t === 'line' || t === 'rect' || t === 'circle';

// Lined / grid / blank, in the order they are offered. Three buttons' worth of width in
// a narrow pad buys nothing — paper is picked once and then left alone — so when the pad
// is compact these collapse to one dropdown showing the current choice.
const PAPERS: { id: Paper; Icon: typeof Pencil; label: string }[] = [
  { id: 'lined', Icon: AlignJustify, label: 'Lined' },
  { id: 'grid', Icon: Grid3x3, label: 'Grid' },
  { id: 'blank', Icon: File, label: 'Blank' },
];

// Thin / medium / thick. Lines and shapes use the pen's widths.
const SIZES = {
  pen: [3, 6, 10],
  highlight: [12, 22, 34],
  eraser: [12, 22, 40],
};
const SIZE_LABELS = ['Thin', 'Medium', 'Thick'];

const PAPER: Record<Paper, CSSProperties> = {
  lined: { backgroundImage: 'repeating-linear-gradient(to bottom, transparent 0 33px, rgba(120,90,60,0.09) 33px 34px)' },
  // 24px squares: roughly one digit per box, for lining up columns
  grid: {
    backgroundImage: 'linear-gradient(rgba(120,90,60,0.11) 1px, transparent 1px), linear-gradient(90deg, rgba(120,90,60,0.11) 1px, transparent 1px)',
    backgroundSize: '24px 24px',
  },
  blank: {},
};

const RING_ON = '0 0 0 3px #FF6B4A';
const RING_OFF = '0 0 0 2px #e5ddd2';

// perfect-freehand outline → an SVG path string we can hand to Path2D.
function svgPathFromStroke(stroke: number[][]): string {
  if (!stroke.length) return '';
  const d = stroke.reduce(
    (acc, [x0, y0], i, arr) => {
      const [x1, y1] = arr[(i + 1) % arr.length];
      acc.push(x0, y0, (x0 + x1) / 2, (y0 + y1) / 2);
      return acc;
    },
    ['M', ...stroke[0], 'Q'] as (string | number)[],
  );
  d.push('Z');
  return d.join(' ');
}

// ── Floating-panel geometry (desktop only) ─────────────────────────────────────
// A bottom sheet is right on a phone and wrong on a laptop. At min(64vh, 560px) and
// full width it buried the question AND every answer option on a state test, and the
// tap-away backdrop meant clicking an option merely closed the pad — so a child could
// not work a sum and answer it without dismissing their working first.
//
// Above sm the pad becomes a draggable, resizable window instead, with no backdrop, so
// the test stays readable and clickable behind it. That is the shape the real test
// engines (TestNav, DRC INSIGHT, Cambium) and Desmos all use for a notepad/calculator.
// BELOW sm nothing changes at all: same sheet, same height, same tap-away backdrop.
type Rect = { x: number; y: number; w: number; h: number };
const GEOM_KEY = 'imasterly_scratchpad_geom';
const MIN_W = 280;
const MIN_H = 240;
const EDGE = 12; // px of viewport the panel may never be dragged past

// Parks in the right-hand gutter beside a max-w-4xl question card rather than over it.
function defaultRect(): Rect {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const w = Math.min(380, vw - 2 * EDGE);
  const h = Math.min(Math.max(360, Math.round(vh * 0.62)), vh - 120);
  return { x: vw - w - 16, y: Math.max(88, Math.round((vh - h) / 2)), w, h };
}

// Never let the panel end up off-screen — after a drag, a resize, a window resize, or a
// stored position carried over from a larger monitor.
function clampRect(r: Rect): Rect {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const w = Math.min(Math.max(MIN_W, r.w), vw - 2 * EDGE);
  const h = Math.min(Math.max(MIN_H, r.h), vh - 2 * EDGE);
  return {
    w, h,
    x: Math.min(Math.max(EDGE, r.x), Math.max(EDGE, vw - w - EDGE)),
    y: Math.min(Math.max(EDGE, r.y), Math.max(EDGE, vh - h - EDGE)),
  };
}

// Where the child last put it. Storage throws in private mode / with site data blocked,
// and a missing value just means "use the default", so every path here is non-fatal.
function loadRect(): Rect | null {
  try {
    const raw = window.localStorage.getItem(GEOM_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<Rect>;
    if ([v.x, v.y, v.w, v.h].every((n) => typeof n === 'number' && Number.isFinite(n))) {
      return v as Rect;
    }
  } catch { /* unreadable storage — fall back to the default position */ }
  return null;
}
function saveRect(r: Rect) {
  try { window.localStorage.setItem(GEOM_KEY, JSON.stringify(r)); } catch { /* ignore */ }
}

// The actual FAB + pad. Rendered ONCE by ScratchpadProvider, only while at
// least one solve screen has opted in via useScratchpad().
function ScratchpadUI() {
  const [open, setOpen] = useState(false);
  const [tool, setTool] = useState<Tool>('pen');
  const [color, setColor] = useState(INK[0]);
  const [hlColor, setHlColor] = useState(HIGHLIGHT[0]);
  const [sizeLevel, setSizeLevel] = useState(1);
  const [paper, setPaper] = useState<Paper>('lined');
  // Window on desktop, sheet on phone. Starts false so SSR and first paint agree; the
  // pad is only ever shown after a click, by which time the effect below has run.
  const [floating, setFloating] = useState(false);
  const [rect, setRect] = useState<Rect | null>(null);
  const rectRef = useRef<Rect | null>(null);
  const dragRef = useRef<{ mode: 'move' | 'resize'; sx: number; sy: number; r: Rect } | null>(null);
  // Which collapsed control is expanded right now. Only one at a time, and only ever in
  // compact mode — a roomy pad shows every option inline and needs none of this.
  const [menu, setMenu] = useState<null | 'size' | 'color' | 'paper'>(null);
  const popRef = useRef<HTMLDivElement | null>(null);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const strokesRef = useRef<Stroke[]>([]);
  const currentRef = useRef<Stroke | null>(null);
  const dprRef = useRef(1);

  const strokeOpts = useCallback((s: Stroke) => ({
    size: s.size,
    // low thinning + heavy streamline = a steady, fairly-uniform pen good for digits
    thinning: s.tool === 'highlight' ? 0 : 0.32,
    smoothing: 0.66,
    streamline: 0.72,
    simulatePressure: true,
    last: true,
    start: { cap: true },
    end: { cap: true },
  }), []);

  const drawStroke = useCallback((ctx: CanvasRenderingContext2D, s: Stroke) => {
    if (s.points.length < 1) return;
    if (isShape(s.tool)) {
      // a shape is just its two corners: where the drag started and where it is now
      const [x0, y0] = s.points[0];
      const [x1, y1] = s.points[s.points.length - 1];
      ctx.strokeStyle = s.color;
      ctx.lineWidth = s.size;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      if (s.tool === 'line') {
        ctx.moveTo(x0, y0);
        ctx.lineTo(x1, y1);
      } else if (s.tool === 'rect') {
        ctx.rect(Math.min(x0, x1), Math.min(y0, y1), Math.abs(x1 - x0), Math.abs(y1 - y0));
      } else {
        ctx.ellipse((x0 + x1) / 2, (y0 + y1) / 2, Math.abs(x1 - x0) / 2, Math.abs(y1 - y0) / 2, 0, 0, Math.PI * 2);
      }
      ctx.stroke();
      return;
    }
    const path = new Path2D(svgPathFromStroke(getStroke(s.points, strokeOpts(s))));
    ctx.globalCompositeOperation = s.tool === 'eraser' ? 'destination-out' : 'source-over';
    ctx.globalAlpha = s.tool === 'highlight' ? 0.32 : 1;
    ctx.fillStyle = s.tool === 'eraser' ? '#000' : s.color;
    ctx.fill(path);
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
  }, [strokeOpts]);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    for (const s of strokesRef.current) drawStroke(ctx, s);
    if (currentRef.current) drawStroke(ctx, currentRef.current);
  }, [drawStroke]);

  // size the canvas to its box (retina-crisp) whenever the sheet opens or resizes
  const fitCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = Math.max(1, window.devicePixelRatio || 1);
    dprRef.current = dpr;
    const r = canvas.getBoundingClientRect();
    canvas.width = Math.round(r.width * dpr);
    canvas.height = Math.round(r.height * dpr);
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    redraw();
  }, [redraw]);

  useEffect(() => {
    if (!open) return;
    // wait a frame so the slide-in has laid the canvas out before measuring
    const t = setTimeout(fitCanvas, 60);
    window.addEventListener('resize', fitCanvas);
    return () => { clearTimeout(t); window.removeEventListener('resize', fitCanvas); };
  }, [open, fitCanvas]);

  // Window or sheet, decided by the same sm breakpoint the toolbars already use.
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 640px)');
    const apply = () => setFloating(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  useEffect(() => { rectRef.current = rect; }, [rect]);

  // Shrinking the browser window must not strand the panel off-screen.
  useEffect(() => {
    if (!floating) return;
    const onResize = () => setRect((cur) => (cur ? clampRect(cur) : cur));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [floating]);

  // Re-fit the canvas after the box changes size. Strokes are stored in the canvas's own
  // CSS pixels so they stay put rather than stretching — the same thing that already
  // happens when the browser window is resized mid-drawing.
  useEffect(() => {
    if (!open || !floating) return;
    const t = setTimeout(fitCanvas, 0);
    return () => clearTimeout(t);
  }, [open, floating, rect?.w, rect?.h, fitCanvas]);

  // Drag by the grab handle, resize by the corner grip. Pointer capture keeps the
  // gesture alive when the cursor leaves the element mid-drag.
  const beginGesture = (mode: 'move' | 'resize') => (e: React.PointerEvent) => {
    if (!floating || !rect) return;
    e.preventDefault();
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    dragRef.current = { mode, sx: e.clientX, sy: e.clientY, r: rect };
  };
  const moveGesture = (e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.sx;
    const dy = e.clientY - d.sy;
    setRect(clampRect(d.mode === 'move'
      ? { ...d.r, x: d.r.x + dx, y: d.r.y + dy }
      : { ...d.r, w: d.r.w + dx, h: d.r.h + dy }));
  };
  const endGesture = () => {
    if (!dragRef.current) return;
    dragRef.current = null;
    if (rectRef.current) saveRect(rectRef.current);
  };

  // A pop-out is temporary: pressing anywhere outside the control strip puts it away.
  // Choosing a value closes it too, so it never stands between a child and the paper.
  useEffect(() => {
    if (!menu) return;
    const onDown = (e: PointerEvent) => {
      if (popRef.current && !popRef.current.contains(e.target as Node)) setMenu(null);
    };
    window.addEventListener('pointerdown', onDown);
    return () => window.removeEventListener('pointerdown', onDown);
  }, [menu]);

  // Nothing may stay expanded across a close, or it reopens looking stuck.
  useEffect(() => { if (!open) setMenu(null); }, [open]);

  // Settle the position BEFORE showing the pad, so it never flashes in one place and
  // jumps to another. matchMedia is read directly rather than through `floating` so a
  // first-ever open cannot race the effect above.
  const openPad = () => {
    if (window.matchMedia('(min-width: 640px)').matches) {
      setRect((cur) => clampRect(cur ?? loadRect() ?? defaultRect()));
    }
    setOpen(true);
  };

  const point = (e: React.PointerEvent): number[] => {
    const r = canvasRef.current!.getBoundingClientRect();
    return [e.clientX - r.left, e.clientY - r.top, e.pressure > 0 ? e.pressure : 0.5];
  };
  const sizeFor = (t: Tool) => SIZES[t === 'highlight' || t === 'eraser' ? t : 'pen'][sizeLevel];

  const onDown = (e: React.PointerEvent) => {
    (e.target as Element).setPointerCapture?.(e.pointerId);
    const p = point(e);
    currentRef.current = {
      tool, size: sizeFor(tool),
      color: tool === 'highlight' ? hlColor : color,
      points: isShape(tool) ? [p, p] : [p],
    };
    redraw();
  };
  const onMove = (e: React.PointerEvent) => {
    const s = currentRef.current;
    if (!s) return;
    if (isShape(s.tool)) s.points[1] = point(e);
    else s.points.push(point(e));
    redraw();
  };
  const onUp = () => {
    const s = currentRef.current;
    if (!s) return;
    currentRef.current = null;
    // A tap with a shape tool makes a shape too small to see, which Undo would then
    // silently spend a press on. Drop it.
    const [x0, y0] = s.points[0];
    const [x1, y1] = s.points[s.points.length - 1];
    if (!isShape(s.tool) || Math.hypot(x1 - x0, y1 - y0) > 3) strokesRef.current.push(s);
    redraw();
  };

  const undo = () => { strokesRef.current.pop(); redraw(); };
  const clear = () => { strokesRef.current = []; currentRef.current = null; redraw(); };
  // Closing KEEPS the work (strokes live in a ref) — reopening shows it again, so an
  // accidental close never loses it. Only the Clear button wipes the paper.
  const close = () => setOpen(false);

  // The color row edits the highlighter's color while it's the tool, the ink otherwise.
  const swatches = tool === 'highlight' ? HIGHLIGHT : INK;
  const activeColor = tool === 'highlight' ? hlColor : color;
  const customColor = !swatches.includes(activeColor);
  const pickColor = (c: string) => {
    if (tool === 'highlight') { setHlColor(c); return; }
    setColor(c);
    if (tool === 'eraser') setTool('pen');
  };
  const colorRing = (selected: boolean) => (selected && tool !== 'eraser' ? RING_ON : RING_OFF);

  // Both a wide screen AND a settled position. If the position were still null we would
  // render a "window" with no left/top, so this keeps the sheet as the safe fallback.
  const asPanel = floating && rect !== null;

  // The toolbar's sm: classes assume the pad is as wide as the viewport. That was true of
  // the bottom sheet and is NOT true of a resizable window: a 380px pad on a 27-inch
  // monitor is every bit as tight as a phone, and the labelled row spilled clean out of
  // the card. So roomy-vs-compact is decided by the PANEL's width. About 620px is where
  // "Pen / Highlight / Eraser / Line / Box / Circle / Undo / Clear" stops fitting on one
  // line — drag the pad wider than that and the words come back.
  const compact = asPanel && (rect?.w ?? 0) < 620;
  // Hide a label that only exists when there is room for it.
  const roomy = (whenNotCompact: string) => (compact ? 'hidden' : whenNotCompact);
  // Buttons lose their padding before they lose their place on the row. At the 280px
  // minimum the six tool buttons must come in to about 28px each to stay on one line:
  // 6 x 28, two dividers and the gaps is roughly 206px of the ~250px the padded row has.
  const btnPad = compact ? 'px-1.5' : 'px-2.5 sm:px-3';

  const toolBtn = (t: Tool, Icon: typeof Pencil, label: string) => (
    <button
      onClick={() => setTool(t)}
      aria-label={label}
      title={label}
      className={`flex items-center gap-1.5 rounded-xl ${btnPad} py-2 text-[13px] font-extrabold transition active:scale-90 ${
        tool === t ? 'bg-brand-coral text-white shadow-sm' : 'bg-brand-cream-2 text-brand-ink'
      }`}
    >
      <Icon size={16} /> <span className={roomy('hidden sm:inline')}>{label}</span>
    </button>
  );

  const paperBtn = (p: Paper, Icon: typeof Pencil, label: string) => (
    <button
      onClick={() => setPaper(p)}
      aria-label={`${label} paper`}
      title={`${label} paper`}
      className={`flex h-9 w-9 items-center justify-center rounded-xl transition active:scale-90 ${
        paper === p ? 'bg-brand-coral text-white shadow-sm' : 'bg-brand-cream-2 text-brand-ink'
      }`}
    >
      <Icon size={16} />
    </button>
  );

  const divider = <span className="mx-0.5 h-6 w-px bg-brand-cream-2" />;

  // Undo / Clear sit at the end of row 1 on wider screens. A phone can't fit them there
  // beside six tools, so they drop to the paper row instead (`phone` renders that copy).
  //
  // A COMPACT PANEL is in the same position as a phone, so it takes the phone copy too.
  // These were briefly put on row 1 to keep Clear prominent, but six tools plus Undo and
  // Clear cannot share a line in a 280px pad: row 1 wrapped and the toolbar became three
  // rows. Beside the three dropdowns on row 2 they fit with room to spare and the toolbar
  // is two rows at every width. Nothing is hidden either way — both rows are always on
  // screen — so Clear loses no prominence by sitting on the second one.
  const histCls = (phone: boolean) => {
    if (compact) return phone ? 'flex' : 'hidden';
    return phone ? 'flex sm:hidden' : 'hidden sm:flex';
  };
  const historyBtns = (phone: boolean) => (
    <>
      <button onClick={undo} aria-label="Undo" title="Undo" className={`${histCls(phone)} ${btnPad} items-center gap-1.5 rounded-xl bg-brand-cream-2 py-2 text-[13px] font-extrabold text-brand-ink active:scale-90`}>
        <Undo2 size={16} /> <span className={roomy('hidden sm:inline')}>Undo</span>
      </button>
      <button onClick={clear} aria-label="Clear" title="Clear" className={`${histCls(phone)} ${btnPad} items-center gap-1.5 rounded-xl bg-brand-cream-2 py-2 text-[13px] font-extrabold text-brand-ink active:scale-90`}>
        <Trash2 size={16} /> <span className={roomy('hidden sm:inline')}>Clear</span>
      </button>
    </>
  );

  return (
    <>
      {/* Floating pull-out tab (hidden while the sheet is open) */}
      <AnimatePresence>
        {!open && (
          <motion.button
            key="fab"
            onClick={openPad}
            initial={{ opacity: 0, scale: 0.8, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 10 }}
            whileTap={{ scale: 0.94 }}
            aria-label="Scratchpad"
            title="Scratchpad"
            // A round pen that widens to "Scratchpad" on hover or keyboard focus (founder, 2026-10-10: the
            // full label sat on top of a test's Next / Review & Submit). A page with its own bottom bar
            // lifts it with --scratchpad-fab-bottom (the state-test page does).
            className="group fixed right-5 z-[70] flex items-center rounded-full bg-gradient-to-br from-[#FF6B6B] to-[#FF9F43] p-3 text-white font-extrabold shadow-xl shadow-brand-coral/30"
            style={{ bottom: 'calc(var(--scratchpad-fab-bottom, 1.25rem) + env(safe-area-inset-bottom))' }}
          >
            <Pencil size={20} />
            <span className="max-w-0 overflow-hidden whitespace-nowrap opacity-0 transition-all duration-200 group-hover:ml-2 group-hover:max-w-[8rem] group-hover:opacity-100 group-focus-visible:ml-2 group-focus-visible:max-w-[8rem] group-focus-visible:opacity-100">
              Scratchpad
            </span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Desktop: a draggable window, no backdrop, page stays live.
          Phone: the original slide-up sheet + tap-away backdrop, untouched. */}
      <AnimatePresence>
        {open && !asPanel && (
          <motion.div
            key="backdrop"
            onClick={close}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[69]"
            style={{ background: 'rgba(20,15,10,0.12)' }}
          />
        )}
        {open && (
          <motion.div
            key="sheet"
            initial={asPanel ? { opacity: 0, scale: 0.97 } : { y: '100%' }}
            animate={asPanel ? { opacity: 1, scale: 1 } : { y: 0 }}
            exit={asPanel ? { opacity: 0, scale: 0.97 } : { y: '100%' }}
            transition={{ type: 'spring', damping: 32, stiffness: 320 }}
            className={asPanel
              ? 'fixed z-[70] flex flex-col overflow-hidden rounded-2xl bg-white shadow-[0_18px_50px_-12px_rgba(0,0,0,0.35)] border border-brand-cream-2'
              : 'fixed inset-x-0 bottom-0 z-[70] flex flex-col rounded-t-3xl bg-white shadow-[0_-16px_40px_-12px_rgba(0,0,0,0.35)] border-t border-brand-cream-2'}
            style={asPanel
              ? { left: rect!.x, top: rect!.y, width: rect!.w, height: rect!.h }
              : { height: 'min(64vh, 560px)', paddingBottom: 'env(safe-area-inset-bottom)' }}
          >
            {/* grab handle — also the drag bar once the pad is a window, and then it
                carries ✕ as well. The close button USED to live at the end of the tool
                row, which is fine on a full-width sheet and not fine in a window: the row
                wraps, so ✕ was the first thing pushed out of sight. On the bar it is
                absolutely placed and cannot be displaced by anything the toolbar does.
                stopPropagation keeps a tap on ✕ from also starting a drag. */}
            <div
              onPointerDown={asPanel ? beginGesture('move') : undefined}
              onPointerMove={asPanel ? moveGesture : undefined}
              onPointerUp={asPanel ? endGesture : undefined}
              onPointerCancel={asPanel ? endGesture : undefined}
              title={asPanel ? 'Drag to move' : undefined}
              className={`relative flex justify-center pt-2.5 pb-1 ${asPanel ? 'cursor-grab touch-none select-none active:cursor-grabbing' : ''}`}
            >
              <div className="h-1.5 w-10 rounded-full bg-brand-cream-2" />
              {asPanel && (
                <button
                  onClick={close}
                  onPointerDown={(e) => e.stopPropagation()}
                  aria-label="Close scratchpad"
                  title="Close"
                  className="absolute right-1.5 top-0 flex h-8 w-8 items-center justify-center text-red-500 transition hover:text-red-600 active:scale-90"
                >
                  <X size={20} strokeWidth={2.75} />
                </button>
              )}
            </div>

            {/* toolbar, row 1: what you draw with */}
            <div className={`flex flex-wrap items-center ${compact ? 'gap-1' : 'gap-1.5 sm:gap-2'} px-3 pb-2`}>
              {toolBtn('pen', Pencil, 'Pen')}
              {toolBtn('highlight', Highlighter, 'Highlight')}
              {toolBtn('eraser', Eraser, 'Eraser')}
              {divider}
              {toolBtn('line', Slash, 'Line')}
              {toolBtn('rect', Square, 'Box')}
              {toolBtn('circle', Circle, 'Circle')}
              <span className="flex-1" />
              {historyBtns(false)}
              {/* As a window the ✕ is on the drag bar instead — see above. */}
              {!asPanel && (
                <button onClick={close} aria-label="Close scratchpad" className="ml-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-cream-2 text-brand-ink active:scale-90">
                  <X size={18} />
                </button>
              )}
            </div>

            {/* toolbar, row 2: how it looks (on a phone the paper group wraps to a 3rd row) */}
            <div className="flex items-center gap-2 px-3 pb-2 flex-wrap">
              {/* COMPACT: pen width, colour and paper each collapse to a single button
                  that pops its options out over the paper and puts them away again as
                  soon as one is chosen. Three controls instead of ten, and the row can
                  never outgrow a narrow pad. ROOMY: everything inline, as before. */}
              {compact ? (
                <div ref={popRef} className="flex w-full items-center gap-2">
                  {/* pen width */}
                  <div className="relative">
                    <button
                      onClick={() => setMenu(menu === 'size' ? null : 'size')}
                      aria-label="Pen width"
                      title="Pen width"
                      className="flex h-9 items-center gap-1 rounded-xl bg-brand-cream-2 px-2 transition active:scale-90"
                    >
                      <span className="rounded-full bg-[#2b2b2b]" style={{ width: 4 + sizeLevel * 5, height: 4 + sizeLevel * 5 }} />
                      <ChevronDown size={12} className="opacity-40" />
                    </button>
                    {menu === 'size' && (
                      <div className="absolute left-0 top-11 z-20 flex gap-1 rounded-xl border border-brand-cream-2 bg-white p-1.5 shadow-xl">
                        {SIZE_LABELS.map((label, lvl) => (
                          <button
                            key={label}
                            onClick={() => { setSizeLevel(lvl); setMenu(null); }}
                            aria-label={`${label} size`}
                            title={label}
                            className={`flex h-9 w-9 items-center justify-center rounded-xl transition active:scale-90 ${
                              sizeLevel === lvl ? 'bg-brand-coral shadow-sm' : 'bg-brand-cream-2'
                            }`}
                          >
                            <span className="rounded-full" style={{ width: 4 + lvl * 5, height: 4 + lvl * 5, background: sizeLevel === lvl ? '#fff' : '#2b2b2b' }} />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* colour */}
                  <div className="relative">
                    <button
                      onClick={() => setMenu(menu === 'color' ? null : 'color')}
                      aria-label="Color"
                      title="Color"
                      className="flex h-9 items-center gap-1 rounded-xl bg-brand-cream-2 px-2 transition active:scale-90"
                    >
                      <span className="h-5 w-5 rounded-full border-2 border-white" style={{ background: activeColor, boxShadow: RING_OFF }} />
                      <ChevronDown size={12} className="opacity-40" />
                    </button>
                    {menu === 'color' && (
                      <div className="absolute left-0 top-11 z-20 flex items-center gap-1.5 rounded-xl border border-brand-cream-2 bg-white p-2 shadow-xl">
                        {swatches.map((c) => (
                          <button
                            key={c}
                            onClick={() => { pickColor(c); setMenu(null); }}
                            aria-label={`color ${c}`}
                            className="h-7 w-7 rounded-full border-[3px] border-white transition active:scale-90"
                            style={{ background: c, boxShadow: colorRing(activeColor === c) }}
                          />
                        ))}
                        <span className="relative h-7 w-7 transition active:scale-90" title="More colors">
                          <span
                            className="block h-7 w-7 rounded-full border-[3px] border-white"
                            style={{
                              background: customColor ? activeColor : 'conic-gradient(#ff4d4d, #ffd54a, #7ee081, #7cc7ff, #9b6bff, #ff8fc7, #ff4d4d)',
                              boxShadow: colorRing(customColor),
                            }}
                          />
                          <input
                            type="color"
                            value={activeColor}
                            onChange={(e) => pickColor(e.target.value)}
                            aria-label="More colors"
                            className="absolute inset-0 h-full w-full cursor-pointer rounded-full opacity-0"
                          />
                        </span>
                      </div>
                    )}
                  </div>

                  {/* paper */}
                  <div className="relative">
                    <button
                      onClick={() => setMenu(menu === 'paper' ? null : 'paper')}
                      aria-label="Paper"
                      title="Paper"
                      className="flex h-9 items-center gap-1 rounded-xl bg-brand-cream-2 px-2 text-brand-ink transition active:scale-90"
                    >
                      {(() => { const P0 = PAPERS.find((x) => x.id === paper) ?? PAPERS[0]; return <P0.Icon size={16} />; })()}
                      <ChevronDown size={12} className="opacity-40" />
                    </button>
                    {menu === 'paper' && (
                      <div className="absolute right-0 top-11 z-20 w-36 overflow-hidden rounded-xl border border-brand-cream-2 bg-white p-1 shadow-xl">
                        {PAPERS.map(({ id, Icon, label }) => (
                          <button
                            key={id}
                            onClick={() => { setPaper(id); setMenu(null); }}
                            className={`flex w-full items-center gap-2 rounded-lg px-2 py-2 text-[13px] font-extrabold transition ${
                              paper === id ? 'bg-brand-coral text-white' : 'text-brand-ink hover:bg-brand-cream-2'
                            }`}
                          >
                            <Icon size={16} /> {label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <span className="flex-1" />
                  {historyBtns(true)}
                </div>
              ) : (
                <>
              {SIZE_LABELS.map((label, lvl) => (
                <button
                  key={label}
                  onClick={() => setSizeLevel(lvl)}
                  aria-label={`${label} size`}
                  title={label}
                  className={`flex h-9 w-9 items-center justify-center rounded-xl transition active:scale-90 ${
                    sizeLevel === lvl ? 'bg-brand-coral shadow-sm' : 'bg-brand-cream-2'
                  }`}
                >
                  <span className="rounded-full" style={{ width: 4 + lvl * 5, height: 4 + lvl * 5, background: sizeLevel === lvl ? '#fff' : '#2b2b2b' }} />
                </button>
              ))}
              {divider}
              {swatches.map((c) => (
                <button
                  key={c}
                  onClick={() => pickColor(c)}
                  aria-label={`color ${c}`}
                  className="h-7 w-7 rounded-full border-[3px] border-white transition active:scale-90"
                  style={{ background: c, boxShadow: colorRing(activeColor === c) }}
                />
              ))}
              {/* Any color: a native color input laid invisibly over a rainbow swatch, so
                  the tap lands on the input itself (iOS won't open a picker for a hidden one).
                  Once a custom color is chosen the swatch shows it. */}
              {/* When the quick-picks are hidden this is the ONLY colour control, so it
                  has to show what you are actually drawing with rather than a rainbow. */}
              <span className="relative h-7 w-7 transition active:scale-90" title="More colors">
                <span
                  className="block h-7 w-7 rounded-full border-[3px] border-white"
                  style={{
                    background: customColor ? activeColor : 'conic-gradient(#ff4d4d, #ffd54a, #7ee081, #7cc7ff, #9b6bff, #ff8fc7, #ff4d4d)',
                    boxShadow: colorRing(customColor),
                  }}
                />
                <input
                  type="color"
                  value={activeColor}
                  onChange={(e) => pickColor(e.target.value)}
                  aria-label="More colors"
                  className="absolute inset-0 h-full w-full cursor-pointer rounded-full opacity-0"
                />
              </span>
              <div className="flex w-full items-center gap-2 sm:ml-auto sm:w-auto">
                {paperBtn('lined', AlignJustify, 'Lined')}
                {paperBtn('grid', Grid3x3, 'Grid')}
                {paperBtn('blank', File, 'Blank')}
                <span className="flex-1 sm:hidden" />
                {historyBtns(true)}
              </div>
                </>
              )}
            </div>

            {/* the paper */}
            <div
              className="relative mx-3 mb-3 flex-1 overflow-hidden rounded-2xl border border-brand-cream-2"
              style={{ backgroundColor: '#FFF8F0', ...PAPER[paper] }}
            >
              <canvas
                ref={canvasRef}
                className="absolute inset-0 h-full w-full touch-none"
                style={{ touchAction: 'none' }}
                onPointerDown={onDown}
                onPointerMove={onMove}
                onPointerUp={onUp}
                onPointerCancel={onUp}
                onPointerLeave={onUp}
              />
            </div>

            {/* Corner grip. Sits over the paper's bottom-right 24px, which is the
                conventional place for one — the cost is that drawing in that exact
                corner resizes instead. */}
            {asPanel && (
              <div
                onPointerDown={beginGesture('resize')}
                onPointerMove={moveGesture}
                onPointerUp={endGesture}
                onPointerCancel={endGesture}
                title="Drag to resize"
                className="absolute bottom-0 right-0 h-6 w-6 cursor-nwse-resize touch-none"
              >
                <span className="pointer-events-none absolute bottom-1.5 right-1.5 block h-2.5 w-2.5 rounded-sm border-b-2 border-r-2 border-brand-cream-2" />
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// ── Central provider + opt-in hook ──────────────────────────────────────────────
// Mount <ScratchpadProvider> ONCE at the app root. Any solve screen calls
// useScratchpad() (one line) to reveal the ✏️; the pad unmounts (ink clears) once no
// screen wants it. Reference-counted, so nested solve screens still show exactly one.
const ScratchpadCtx = createContext<{ acquire: () => () => void } | null>(null);

export function ScratchpadProvider({ children }: { children: ReactNode }) {
  const [wanters, setWanters] = useState(0);
  const acquire = useCallback(() => {
    setWanters((n) => n + 1);
    return () => setWanters((n) => Math.max(0, n - 1));
  }, []);
  return (
    <ScratchpadCtx.Provider value={{ acquire }}>
      {children}
      {wanters > 0 && <ScratchpadUI />}
    </ScratchpadCtx.Provider>
  );
}

/** Call once at the top of any screen where a kid should be able to scratch-work. */
export function useScratchpad(active: boolean = true) {
  const ctx = useContext(ScratchpadCtx);
  useEffect(() => {
    if (!ctx || !active) return;
    return ctx.acquire();
  }, [ctx, active]);
}
